import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle } from "@phosphor-icons/react";
import { useLanguage } from "@/context/LanguageContext";

export default function ColorSelector() {
  const { t } = useLanguage();
  const vinylColors = t.colorSelector.vinylColors;
  const fabricColors = t.colorSelector.fabricColors;

  const [selectedVinyl, setSelectedVinyl] = useState(vinylColors[0]);
  const [selectedFabric, setSelectedFabric] = useState(fabricColors[0]);
  const [activeTab, setActiveTab] = useState("vinyl");

  const selected = activeTab === "vinyl" ? selectedVinyl : selectedFabric;

  return (
    <section id="materiales" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="color-selector-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700] mb-4 font-light">{t.colorSelector.overline}</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-white tracking-tight">{t.colorSelector.title}</h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Booth Preview */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-[#121212] border border-white/10 p-8 flex flex-col items-center justify-center"
            data-testid="color-preview-area"
          >
            {/* Booth image with color overlay */}
            <div className="relative overflow-hidden" data-testid="booth-preview">
              <img
                src="https://customer-assets.emergentagent.com/job_tapiceria-confort/artifacts/045eni3u_80515.jpg"
                alt="Booth preview"
                className="w-full object-cover"
                style={{ filter: "grayscale(100%) brightness(0.9)" }}
              />
              <div
                className="absolute inset-0 transition-colors duration-500"
                style={{ backgroundColor: selected.hex, mixBlendMode: "multiply", opacity: 0.6 }}
              />
              <div
                className="absolute inset-0 transition-colors duration-500"
                style={{ backgroundColor: selected.hex, mixBlendMode: "color", opacity: 0.5 }}
              />
            </div>

            {/* Selected color info */}
            <div className="mt-6 flex items-center gap-3 self-start">
              <div
                className="w-8 h-8 rounded-full border-2 border-[#FFD700] transition-colors duration-500"
                style={{ backgroundColor: selected.hex }}
              />
              <div>
                <p className="text-white font-['Cormorant_Garamond'] text-xl" data-testid="selected-color-name">
                  {selected.name}
                </p>
                <p className="text-zinc-400 text-xs uppercase tracking-wider">
                  {selected.texture} | {activeTab === "vinyl" ? t.colorSelector.vinyl : t.colorSelector.fabric}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Color Picker Panel */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Tabs defaultValue="vinyl" onValueChange={(v) => setActiveTab(v)}>
              <TabsList className="bg-[#1A1A1A] border border-white/10 p-1 mb-8 w-full">
                <TabsTrigger value="vinyl" className="flex-1 data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-zinc-400 uppercase tracking-wider text-sm" data-testid="tab-vinyl">{t.colorSelector.vinyl}</TabsTrigger>
                <TabsTrigger value="fabric" className="flex-1 data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-zinc-400 uppercase tracking-wider text-sm" data-testid="tab-fabric">{t.colorSelector.fabric}</TabsTrigger>
              </TabsList>
              <TabsContent value="vinyl">
                <ColorGrid colors={vinylColors} selected={selectedVinyl} onSelect={setSelectedVinyl} prefix="vinyl" />
              </TabsContent>
              <TabsContent value="fabric">
                <ColorGrid colors={fabricColors} selected={selectedFabric} onSelect={setSelectedFabric} prefix="fabric" />
              </TabsContent>
            </Tabs>
            <div className="mt-8 p-6 bg-[#121212] border border-white/10">
              <p className="text-zinc-400 text-sm font-light leading-relaxed">{t.colorSelector.selectedInfo}</p>
              <a href="#cotizacion" className="inline-flex items-center gap-2 mt-4 text-[#FFD700] text-sm uppercase tracking-wider hover:underline" data-testid="selector-cta">{t.colorSelector.ctaSample}</a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ColorGrid({ colors, selected, onSelect, prefix }) {
  return (
    <div className="grid grid-cols-4 gap-4">
      {colors.map((color) => (
        <button key={color.id} onClick={() => onSelect(color)} className="group flex flex-col items-center gap-2 focus:outline-none" data-testid={`swatch-${prefix}-${color.id}`}>
          <div className="relative">
            <div className={`w-14 h-14 rounded-full transition-all duration-300 ${selected.id === color.id ? "ring-2 ring-[#FFD700] ring-offset-2 ring-offset-[#0A0A0A] scale-110" : "ring-1 ring-white/20 hover:ring-white/50"}`} style={{ backgroundColor: color.hex }} />
            {selected.id === color.id && <CheckCircle size={20} weight="fill" className="absolute -top-1 -right-1 text-[#FFD700]" />}
          </div>
          <span className={`text-xs text-center leading-tight transition-colors ${selected.id === color.id ? "text-[#FFD700]" : "text-zinc-500 group-hover:text-zinc-300"}`}>{color.name}</span>
        </button>
      ))}
    </div>
  );
}
